/* PCIS Health — Details (route /supply/pcis-health/details). Paginated order-level
   rows under the same from/to/courier/account window as Summary, plus an
   "Only failed" toggle. AWB renders as a blue tracking link + copy icon, grouped
   under the courier name ("Courier & AWB"), matching the Forward Shipments table.

   Two things learned the hard way here:
   1. Links use inline `style` (not sx/className) for their colour. This page
      lives inside `.supply-scope`, whose stylesheet has `.supply-scope a { color:
      inherit }` (specificity 0,1,1) — that beats emotion's sx colour class
      (0,1,0), so an sx-coloured link renders BLACK here even though the identical
      markup is blue in Forward Shipments (which isn't scoped). Inline style wins.
   2. Columns are curated from the confirmed /health/orders shape — NOT derived
      from Object.keys(rows[0]). Rows are heterogeneous: a FAILED row has no
      awbNumber / labelUrl, and the newest row (row[0]) is usually a fresh
      failure, so key-sniffing silently dropped those columns.

   `placementStatus` ("SUCCESS"/"FAILED") is the real outcome; `orderStatus`
   (a small int like 1/2/28) is an unrelated internal code, so it isn't shown. */
import React, {useState} from 'react'
import {Box, Stack, Tooltip, Typography} from '@mui/material'
import {Link} from 'react-router-dom'
import PageTitle from '../../../components/PageTitle'
import {PageHeader, LoadErrorPanel} from '../components'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import CopyIcon from '../../../common/CopyIcon'
import {useListPcisCourierAccountsQuery, usePcisHealthOrdersQuery} from '../../../redux/supplyApiSlice'
import PcisHealthFilterBar, {defaultFilters} from './PcisHealthFilterBar'

const linkStyle = {color: '#1d4ed8', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer'}

const PLACEMENT_PALETTE = {
  SUCCESS: {bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0'},
  FAILED: {bg: '#fef2f2', fg: '#dc2626', border: '#fecaca'},
  default: {bg: '#f9fafb', fg: '#6b7280', border: '#e5e7eb'},
}

const PlacementStatusChip = ({status}) => {
  if (!status) return <Box sx={{fontSize: 12, color: 'text.disabled'}}>—</Box>
  const p = PLACEMENT_PALETTE[String(status).toUpperCase()] || PLACEMENT_PALETTE.default
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: '8px',
        py: '3px',
        borderRadius: '4px',
        fontSize: '10.5px',
        fontWeight: 700,
        letterSpacing: '0.3px',
        textTransform: 'uppercase',
        bgcolor: p.bg,
        color: p.fg,
        border: `1px solid ${p.border}`,
      }}
    >
      {status}
    </Box>
  )
}

const AwbLink = ({awb, id}) => (
  <Link
    to={`/order-detail/${awb}?id=${id}`}
    target='_blank'
    rel='noopener noreferrer'
    style={{...linkStyle, fontSize: '12px', letterSpacing: '0.02em', textDecorationColor: '#93c5fd'}}
  >
    {awb}
  </Link>
)

const muted = () => <Box sx={{fontSize: 12, color: 'text.disabled'}}>—</Box>

const fmtDateTime = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  return isNaN(d) ? v : d.toLocaleString('en-IN', {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'})
}

const fmtMoney = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`)

const COLUMNS = [
  {
    field: 'awbNumber',
    headerName: 'Courier & AWB',
    minWidth: 210,
    flex: 1.2,
    sortable: false,
    renderCell: ({row}) => (
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px', py: 0.5}}>
        {row.courierCode ? (
          <Typography sx={{fontSize: 12.5, fontWeight: 700, lineHeight: 1.3}}>{row.courierCode}</Typography>
        ) : null}
        {row.awbNumber ? (
          <Box sx={{display: 'flex', alignItems: 'center', gap: '2px'}}>
            <AwbLink awb={row.awbNumber} id={row.id} />
            <CopyIcon text={row.awbNumber} title='Copy AWB' color='#1d4ed8' />
          </Box>
        ) : (
          <Typography sx={{fontSize: 11, color: 'text.disabled'}}>No AWB</Typography>
        )}
      </Box>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    minWidth: 140,
    sortable: false,
    renderCell: ({row}) => <Typography sx={{fontSize: 12, lineHeight: 1.3}}>{fmtDateTime(row.createdAt)}</Typography>,
  },
  {
    field: 'placementStatus',
    headerName: 'Placement',
    minWidth: 115,
    sortable: false,
    renderCell: ({row}) => <PlacementStatusChip status={row.placementStatus} />,
  },
  {
    field: 'labelUrl',
    headerName: 'Label',
    minWidth: 110,
    sortable: false,
    renderCell: ({row}) =>
      row.labelUrl ? (
        <a href={row.labelUrl} target='_blank' rel='noopener noreferrer' style={{...linkStyle, fontSize: '12px'}}>
          View label
        </a>
      ) : (
        muted()
      ),
  },
  {
    field: 'referenceNumber',
    headerName: 'Reference',
    minWidth: 170,
    flex: 1,
    sortable: false,
    renderCell: ({row}) =>
      row.referenceNumber ? (
        <Box sx={{display: 'flex', alignItems: 'center', gap: '2px'}}>
          <Typography sx={{fontSize: 12, lineHeight: 1.3, wordBreak: 'break-all'}}>{row.referenceNumber}</Typography>
          <CopyIcon text={row.referenceNumber} title='Copy reference' />
        </Box>
      ) : (
        muted()
      ),
  },
  {
    field: 'failureReason',
    headerName: 'Failure Reason',
    minWidth: 220,
    flex: 1.4,
    sortable: false,
    renderCell: ({row}) =>
      row.failureReason ? (
        <Tooltip title={row.failureReason}>
          <Typography
            sx={{
              fontSize: 12,
              color: '#b91c1c',
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {row.failureReason}
          </Typography>
        </Tooltip>
      ) : (
        muted()
      ),
  },
  {
    field: 'orderType',
    headerName: 'Type',
    minWidth: 130,
    sortable: false,
    renderCell: ({row}) => (
      <Stack spacing={0.25}>
        <Typography sx={{fontSize: 12, fontWeight: 600, lineHeight: 1.2}}>{row.orderType || '—'}</Typography>
        <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2}}>{row.deliveryType || ''}</Typography>
      </Stack>
    ),
  },
  {
    field: 'invoiceValue',
    headerName: 'Value',
    minWidth: 120,
    sortable: false,
    renderCell: ({row}) => (
      <Stack spacing={0.25}>
        <Typography sx={{fontSize: 12, fontWeight: 600, lineHeight: 1.2}}>{fmtMoney(row.invoiceValue)}</Typography>
        {row.codValue ? (
          <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2}}>COD {fmtMoney(row.codValue)}</Typography>
        ) : null}
      </Stack>
    ),
  },
  {
    field: 'pickupPincode',
    headerName: 'Route (PIN)',
    minWidth: 140,
    sortable: false,
    renderCell: ({row}) => (
      <Typography sx={{fontSize: 12, lineHeight: 1.3}}>
        {row.pickupPincode || '—'} <Box component='span' sx={{color: 'text.disabled'}}>→</Box> {row.dropPincode || '—'}
      </Typography>
    ),
  },
  {
    field: 'channelType',
    headerName: 'Channel',
    minWidth: 150,
    flex: 1,
    sortable: false,
    renderCell: ({row}) => (
      <Stack spacing={0.25}>
        <Typography sx={{fontSize: 12, fontWeight: 600, lineHeight: 1.2}}>{row.channelType || '—'}</Typography>
        {row.channelOrderId ? (
          <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2, wordBreak: 'break-all'}}>{row.channelOrderId}</Typography>
        ) : null}
      </Stack>
    ),
  },
  {
    field: 'accountCode',
    headerName: 'Account',
    minWidth: 170,
    flex: 1,
    sortable: false,
    renderCell: ({row}) => <Typography sx={{fontSize: 12, lineHeight: 1.3, wordBreak: 'break-all'}}>{row.accountCode || '—'}</Typography>,
  },
]

export default function PcisHealthDetails() {
  const [filters, setFilters] = useState(() => defaultFilters({onlyFailed: false}))
  const [page, setPage] = useState(0) // 0-based — matches both ProzoDataGrid and the API directly
  const [sizePerPage, setSizePerPage] = useState(20) // default 20; API max is 100
  const {data: accounts} = useListPcisCourierAccountsQuery()
  const {data, isFetching, isError, error, refetch} = usePcisHealthOrdersQuery({...filters, page, size: sizePerPage})

  const rows = Array.isArray(data) ? data : data?.orders || data?.content || data?.items || []
  // Total drives the page count. Prefer the API's own totals; fall back to
  // totalPages*size, and only then to rows.length (which would otherwise pin the
  // grid to a single page and disable the Next button).
  const total =
    data?.totalOrders ??
    data?.total ??
    data?.totalElements ??
    (data?.totalPages != null ? data.totalPages * sizePerPage : rows.length)

  const onFiltersChange = (next) => {
    setFilters(next)
    setPage(0) // any filter change restarts pagination
  }

  const onSizeChange = (size) => {
    setSizePerPage(size)
    setPage(0)
  }

  return (
    <>
      <PageTitle>PCIS — Details</PageTitle>
      <PageHeader title='PCIS — Order Details' sub='Row-level orders for the Direct Integration (PCIS) courier accounts.' />

      <PcisHealthFilterBar filters={filters} onChange={onFiltersChange} accounts={accounts} showOnlyFailed />

      {isError ? (
        <LoadErrorPanel message={error?.message || 'Could not load orders.'} onRetry={refetch} />
      ) : (
        <ProzoDataGrid
          columns={COLUMNS}
          rows={rows}
          getRowId={(r) => r.id || r.trackingId || JSON.stringify(r)}
          rowCount={total}
          page={page}
          setPage={setPage}
          sizePerPage={sizePerPage}
          setSizePerPage={onSizeChange}
          pagination
          loading={isFetching}
        />
      )}
    </>
  )
}
